/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * FieldsSection — Accordion 4: field visibility toggles + drag-and-drop reorder.
 *
 * All table fields are shown here, including those assigned in Field Mapping.
 * Any field can be toggled visible/hidden. Visible fields can be reordered via drag.
 */

import React, { useState } from 'react';

export default function FieldsSection({ config, updateConfig, fields = [], theme }) {
  // Default: all fields visible when visible_fields has not been configured yet
  const storedVisible = config.visible_fields != null
    ? config.visible_fields
    : fields.map(f => f.name);

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  function save(visibleNames) {
    const hiddenNames = fields
      .filter(f => !visibleNames.includes(f.name))
      .map(f => f.name);
    updateConfig({
      visible_fields: visibleNames,
      layout_config: {
        ...(config.layout_config || {}),
        hidden_fields: hiddenNames,
      },
    });
  }

  function toggleField(fieldName) {
    const updated = storedVisible.includes(fieldName)
      ? storedVisible.filter(n => n !== fieldName)
      : [...storedVisible, fieldName];
    save(updated);
  }

  const visibleObjs = storedVisible
    .map(name => fields.find(f => f.name === name))
    .filter(Boolean);
  const hiddenObjs = fields.filter(f => !storedVisible.includes(f.name));

  function handleDragStart(e, idx) {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
  }

  // onDragEnter fires once when the pointer enters a new element — reliable in both
  // directions. onDragOver fires continuously but can miss events when moving upward,
  // so we use it only to keep the drop permission active (required by the browser).
  function handleDragEnter(e, idx) {
    e.preventDefault();
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e, toIdx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === toIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    const arr = [...storedVisible];
    const [item] = arr.splice(dragIdx, 1);
    // After removing the item, every index above dragIdx shifts down by 1.
    // When dropping below the original position (toIdx > dragIdx), compensate.
    const insertAt = toIdx > dragIdx ? toIdx - 1 : toIdx;
    arr.splice(insertAt, 0, item);
    save(arr);
    setDragIdx(null);
    setDragOverIdx(null);
  }

  function handleDragEnd() {
    setDragIdx(null);
    setDragOverIdx(null);
  }

  if (fields.length === 0) {
    return <div className="text-muted small">No fields available.</div>;
  }

  const accentColor = theme?.primary || '#6366f1';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          {visibleObjs.length} of {fields.length} fields visible
          {fields.length > 0 && <> · drag <i className="bi bi-grip-vertical" /> to reorder</>}
        </small>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => save(fields.map(f => f.name))}
          >
            Show All
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => save([])}
          >
            Hide All
          </button>
        </div>
      </div>

      {/* Visible fields — draggable */}
      <div className="border rounded overflow-hidden mb-2">
        {visibleObjs.length === 0 && (
          <div className="px-3 py-3 text-muted small text-center">
            No fields visible. Toggle fields below to show them.
          </div>
        )}
        {visibleObjs.map((field, idx) => {
          const isDragging   = dragIdx === idx;
          const isDropTarget = dragOverIdx === idx && dragIdx !== idx;
          return (
            <div
              key={field.name}
              draggable
              onDragStart={e => handleDragStart(e, idx)}
              onDragEnter={e => handleDragEnter(e, idx)}
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              className="d-flex align-items-center gap-3 px-3 py-2"
              style={{
                background: isDragging ? '#f1f5f9' : '#fff',
                borderBottom: idx < visibleObjs.length - 1 ? '1px solid #e2e8f0' : 'none',
                borderTop: isDropTarget ? `2px solid ${accentColor}` : '2px solid transparent',
                opacity: isDragging ? 0.5 : 1,
                transition: 'border-color 0.1s, opacity 0.1s',
                cursor: 'grab',
              }}
            >
              <i className="bi bi-grip-vertical text-muted flex-shrink-0" style={{ fontSize: 16 }} />
              <div className="form-check form-switch mb-0 flex-shrink-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked
                  onChange={() => toggleField(field.name)}
                  id={`field-vis-${field.name}`}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <label
                className="flex-grow-1 mb-0 small fw-semibold"
                htmlFor={`field-vis-${field.name}`}
                style={{ cursor: 'pointer', color: '#1e293b' }}
              >
                {field.display_name || field.name}
                <span className="text-muted fw-normal ms-2" style={{ fontSize: 11 }}>
                  {field.data_type}
                </span>
              </label>
              <span
                className="badge rounded-pill flex-shrink-0"
                style={{ background: accentColor + '20', color: accentColor, fontSize: 11 }}
              >
                {idx + 1}
              </span>
            </div>
          );
        })}
      </div>

      {/* Hidden fields */}
      {hiddenObjs.length > 0 && (
        <div className="border rounded overflow-hidden">
          <div className="px-3 py-1 bg-light border-bottom">
            <small className="text-muted fw-semibold">Hidden fields</small>
          </div>
          {hiddenObjs.map((field, idx) => (
            <div
              key={field.name}
              className="d-flex align-items-center gap-3 px-3 py-2"
              style={{
                background: '#f8fafc',
                borderBottom: idx < hiddenObjs.length - 1 ? '1px solid #e2e8f0' : 'none',
              }}
            >
              <span style={{ width: 16 }} />
              <div className="form-check form-switch mb-0 flex-shrink-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={false}
                  onChange={() => toggleField(field.name)}
                  id={`field-vis-${field.name}`}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <label
                className="flex-grow-1 mb-0 small"
                htmlFor={`field-vis-${field.name}`}
                style={{ cursor: 'pointer', color: '#94a3b8' }}
              >
                {field.display_name || field.name}
                <span className="fw-normal ms-2" style={{ fontSize: 11 }}>{field.data_type}</span>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
